import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SEO from '../components/shared/SEO';

/**
 * Privacy Policy Page
 */
export default function Privacy() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  return (
    <div className="bg-midnight min-h-screen">
      <SEO
        title="Privacy Policy"
        description="Raflr's privacy policy explains how we collect, use, and protect your personal information on our competition marketplace."
        path="/privacy"
      />
      <div className="page-container py-12 md:py-20">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-display text-cream mb-4">
            Privacy Policy
          </h1>
          <p className="text-cream/50 mb-12">
            Last updated: 20 January 2026
          </p>

          <div className="prose prose-invert prose-cream max-w-none space-y-8">
            {/* Introduction */}
            <section id="introduction">
              <h2 className="text-xl font-semibold text-cream mb-4">1. Introduction</h2>

              <p className="text-cream/70 mb-4">
                Raflr ("we", "us", "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform at raflr.ie (the "Platform").
              </p>

              <p className="text-cream/70 mb-4">
                We process personal data in accordance with the General Data Protection Regulation (GDPR) and the Irish Data Protection Acts 2018. By using the Platform, you agree to the collection and use of information in accordance with this policy.
              </p>

              <p className="text-cream/70">
                <strong className="text-cream">Data Controller:</strong> Raflr is the data controller responsible for your personal data. For contact details, see section 11.
              </p>
            </section>

            {/* Information We Collect */}
            <section id="information-collected">
              <h2 className="text-xl font-semibold text-cream mb-4">2. Information We Collect</h2>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">2.1 Information You Provide:</strong>
              </p>
              <ul className="list-disc list-inside text-cream/70 space-y-2 ml-4 mb-4">
                <li><strong className="text-cream">Account Information:</strong> Name, email address, phone number, and profile photo when you register</li>
                <li><strong className="text-cream">Seller Information:</strong> Bank account details (processed by Stripe), pickup address for item collection</li>
                <li><strong className="text-cream">Competition Listings:</strong> Item photos, descriptions, pricing, and location information</li>
                <li><strong className="text-cream">Communications:</strong> Messages sent between buyers and sellers through our platform</li>
                <li><strong className="text-cream">Support Requests:</strong> Information you provide when contacting customer support</li>
              </ul>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">2.2 Information Collected Automatically:</strong>
              </p>
              <ul className="list-disc list-inside text-cream/70 space-y-2 ml-4 mb-4">
                <li><strong className="text-cream">Device Information:</strong> Browser type, operating system, device identifiers</li>
                <li><strong className="text-cream">Usage Data:</strong> Pages visited, competitions viewed, entries made, time spent on Platform</li>
                <li><strong className="text-cream">Location Data:</strong> General location based on IP address</li>
                <li><strong className="text-cream">Cookies:</strong> See section 7 for details on our cookie usage</li>
              </ul>

              <p className="text-cream/70">
                <strong className="text-cream">2.3 Information from Third Parties:</strong> We receive verification data from Stripe when you complete seller onboarding, and authentication data from Firebase for account security.
              </p>
            </section>

            {/* How We Use Information */}
            <section id="how-we-use">
              <h2 className="text-xl font-semibold text-cream mb-4">3. How We Use Your Information</h2>

              <p className="text-cream/70 mb-4">We use your information for the following purposes:</p>

              <ul className="list-disc list-inside text-cream/70 space-y-2 ml-4">
                <li><strong className="text-cream">Provide Services:</strong> Create and manage your account, process entries and payments, facilitate handovers between winners and sellers</li>
                <li><strong className="text-cream">Communications:</strong> Send competition updates, winner notifications, handover reminders, and customer support responses</li>
                <li><strong className="text-cream">Security:</strong> Verify identity, prevent fraud, enforce our Terms & Conditions</li>
                <li><strong className="text-cream">Improvements:</strong> Analyze usage patterns to improve Platform features and user experience</li>
                <li><strong className="text-cream">Legal Compliance:</strong> Meet regulatory requirements and respond to legal requests</li>
                <li><strong className="text-cream">Marketing:</strong> With your consent, send promotional communications about competitions and features</li>
              </ul>
            </section>

            {/* Legal Basis */}
            <section id="legal-basis">
              <h2 className="text-xl font-semibold text-cream mb-4">4. Legal Basis for Processing</h2>

              <p className="text-cream/70 mb-4">Under GDPR, we process your data based on:</p>

              <ul className="list-disc list-inside text-cream/70 space-y-2 ml-4">
                <li><strong className="text-cream">Contract Performance:</strong> Processing necessary to provide our services to you (account management, payment processing, competition participation)</li>
                <li><strong className="text-cream">Legitimate Interests:</strong> Processing necessary for our legitimate interests (fraud prevention, security, Platform improvement) where not overridden by your rights</li>
                <li><strong className="text-cream">Legal Obligation:</strong> Processing required to comply with laws (tax records, regulatory requirements)</li>
                <li><strong className="text-cream">Consent:</strong> Where you have given specific consent (marketing communications)</li>
              </ul>
            </section>

            {/* Information Sharing */}
            <section id="sharing">
              <h2 className="text-xl font-semibold text-cream mb-4">5. Information Sharing</h2>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">5.1 Other Users:</strong>
              </p>
              <ul className="list-disc list-inside text-cream/70 space-y-1 ml-4 mb-4">
                <li>Sellers see buyer names and contact information when necessary for handover</li>
                <li>Winners see seller pickup addresses to arrange collection</li>
                <li>Public competition listings show seller display names</li>
              </ul>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">5.2 Service Providers:</strong>
              </p>
              <ul className="list-disc list-inside text-cream/70 space-y-1 ml-4 mb-4">
                <li><strong className="text-cream">Stripe:</strong> Payment processing and seller identity verification</li>
                <li><strong className="text-cream">Firebase (Google):</strong> Authentication, database, and hosting services</li>
                <li><strong className="text-cream">SendGrid:</strong> Email delivery services</li>
                <li><strong className="text-cream">Twilio:</strong> SMS notifications and phone verification</li>
              </ul>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">5.3 Legal Requirements:</strong> We may disclose information if required by law, court order, or government request, or to protect our rights, property, or safety.
              </p>

              <p className="text-cream/70">
                <strong className="text-cream">5.4 Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.
              </p>
            </section>

            {/* Data Retention */}
            <section id="retention">
              <h2 className="text-xl font-semibold text-cream mb-4">6. Data Retention</h2>

              <p className="text-cream/70 mb-4">We retain your information for as long as necessary to:</p>

              <ul className="list-disc list-inside text-cream/70 space-y-1 ml-4 mb-4">
                <li>Provide our services while your account is active</li>
                <li>Comply with legal obligations (typically 7 years for financial records)</li>
                <li>Resolve disputes and enforce agreements</li>
              </ul>

              <p className="text-cream/70 mb-4">
                <strong className="text-cream">Account Deletion:</strong> You may request account deletion by contacting us. Upon deletion, we will remove or anonymise your personal data within 30 days, except where retention is required by law.
              </p>

              <p className="text-cream/70">
                <strong className="text-cream">Competition Records:</strong> Records of competition entries and outcomes are retained for legal compliance purposes even after account deletion.
              </p>
            </section>

            {/* Cookies */}
            <section id="cookies">
              <h2 className="text-xl font-semibold text-cream mb-4">7. Cookies and Tracking</h2>

              <p className="text-cream/70 mb-4">We use cookies and similar technologies to:</p>

              <ul className="list-disc list-inside text-cream/70 space-y-1 ml-4 mb-4">
                <li><strong className="text-cream">Essential Cookies:</strong> Enable core functionality like authentication and security</li>
                <li><strong className="text-cream">Preference Cookies:</strong> Remember your settings and preferences</li>
                <li><strong className="text-cream">Analytics Cookies:</strong> Understand how users interact with the Platform</li>
              </ul>

              <p className="text-cream/70 mb-4">
                You can control cookies through your browser settings. Disabling essential cookies may affect Platform functionality.
              </p>

              <p className="text-cream/70">
                <strong className="text-cream">Do Not Track:</strong> We do not currently respond to "Do Not Track" browser signals.
              </p>
            </section>

            {/* Your Rights */}
            <section id="your-rights">
              <h2 className="text-xl font-semibold text-cream mb-4">8. Your Rights</h2>

              <p className="text-cream/70 mb-4">Under GDPR, you have the following rights:</p>

              <ul className="list-disc list-inside text-cream/70 space-y-2 ml-4 mb-4">
                <li><strong className="text-cream">Access:</strong> Request a copy of the personal data we hold about you</li>
                <li><strong className="text-cream">Rectification:</strong> Request correction of inaccurate or incomplete data</li>
                <li><strong className="text-cream">Erasure:</strong> Request deletion of your personal data ("right to be forgotten")</li>
                <li><strong className="text-cream">Restriction:</strong> Request limitation of processing in certain circumstances</li>
                <li><strong className="text-cream">Portability:</strong> Receive your data in a structured, machine-readable format</li>
                <li><strong className="text-cream">Objection:</strong> Object to processing based on legitimate interests or direct marketing</li>
                <li><strong className="text-cream">Withdraw Consent:</strong> Withdraw consent at any time where processing is based on consent</li>
              </ul>

              <p className="text-cream/70">
                To exercise these rights, contact us at <a href="mailto:privacy@raflr.ie" className="text-primary hover:underline">privacy@raflr.ie</a>. We will respond within 30 days.
              </p>
            </section>

            {/* Security */}
            <section id="security">
              <h2 className="text-xl font-semibold text-cream mb-4">9. Data Security</h2>

              <p className="text-cream/70 mb-4">We implement appropriate technical and organisational measures to protect your data, including:</p>

              <ul className="list-disc list-inside text-cream/70 space-y-1 ml-4 mb-4">
                <li>Encryption of data in transit (TLS/SSL)</li>
                <li>Encryption of sensitive data at rest</li>
                <li>Access controls and authentication</li>
                <li>Regular security assessments</li>
                <li>Secure payment processing through Stripe (PCI-DSS compliant)</li>
              </ul>

              <p className="text-cream/70">
                While we implement safeguards, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security.
              </p>
            </section>

            {/* International Transfers */}
            <section id="transfers">
              <h2 className="text-xl font-semibold text-cream mb-4">10. International Data Transfers</h2>

              <p className="text-cream/70 mb-4">
                Your data may be processed by our service providers in countries outside the European Economic Area (EEA). When this occurs, we ensure appropriate safeguards are in place:
              </p>

              <ul className="list-disc list-inside text-cream/70 space-y-1 ml-4">
                <li>Standard Contractual Clauses approved by the European Commission</li>
                <li>Adequacy decisions where applicable</li>
                <li>Data Processing Agreements with all service providers</li>
              </ul>
            </section>

            {/* Contact */}
            <section id="contact">
              <h2 className="text-xl font-semibold text-cream mb-4">11. Contact Us</h2>

              <p className="text-cream/70 mb-4">
                For questions about this Privacy Policy or to exercise your data rights:
              </p>

              <div className="text-cream/70 space-y-2">
                <p>Email: <a href="mailto:privacy@raflr.ie" className="text-primary hover:underline">privacy@raflr.ie</a></p>
                <p>General enquiries: <a href="mailto:support@raflr.ie" className="text-primary hover:underline">support@raflr.ie</a></p>
              </div>

              <p className="text-cream/70 mt-4">
                <strong className="text-cream">Complaints:</strong> You have the right to lodge a complaint with the Irish Data Protection Commission if you believe your data has been mishandled. Visit <a href="https://www.dataprotection.ie" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">www.dataprotection.ie</a> for more information.
              </p>
            </section>

            {/* Changes */}
            <section id="changes">
              <h2 className="text-xl font-semibold text-cream mb-4">12. Changes to This Policy</h2>

              <p className="text-cream/70 mb-4">
                We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Last updated" date.
              </p>

              <p className="text-cream/70">
                For material changes, we will notify registered users via email at least 14 days before the changes take effect.
              </p>
            </section>
          </div>

          {/* Link to Terms */}
          <div className="mt-12 pt-8 border-t border-cream/10">
            <p className="text-cream/50">
              See also our <Link to="/terms" className="text-primary hover:underline">Terms & Conditions</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
